import classNames from 'classnames'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useMemo, useRef, type ReactNode } from 'react'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { Timing } from '@opallabs/gem'
import { parseCalendarDate } from '../monthGrid'
import { type ResolveChosenColor } from '../resolveItemColor'
import {
  type CalendarItem,
  type CalendarItemKind,
  type OnCalendarItemEdit,
  type ResolveDisplay,
  type ResolveImageUrl,
} from '../types'
import { DayPanel } from './DayPanel'
import { buildPanelFormatters } from './formatters'
import { ItemPanel } from './ItemPanel'
import { buildGridOrder } from './panelData'
import * as styles from './styles'
import {
  calendarPanelDepth,
  isCalendarPanelShowing,
  type CalendarPanelState,
  type CalendarPanelStep,
  type CalendarPanelTab,
} from './useCalendarPanel'

export { DayPanel } from './DayPanel'
export { ItemPanel } from './ItemPanel'
export {
  PANEL_INLINE_SIZE,
  selectors as calendarPanelSelectors,
} from './styles'

/**
 * The way a panel slides in and the old one slides out: deeper pushes to the
 * left, the way a phone does; back up the stack pushes to the right. Nothing
 * moves when the stack neither deepens nor shallows, or when motion is to be
 * kept down.
 */
const push = {
  center: { x: 0 },
  enter: (direction: number) => ({ x: `${direction * 100}%` }),
  exit: (direction: number) => ({ x: `${-direction * 100}%` }),
}

/** The same slide, at the same pace, as the filter drawer's panes: CSS `ease`. */
const pushTransition = {
  duration: Timing.Default / 1000,
  ease: [0.25, 0.1, 0.25, 1],
} as const

/**
 * Which way the next panel pushes, from where the stack was and where it is
 * now. Kept in refs rather than state: the direction has to be right in the
 * very render the panel changes, and must not change again while the old
 * panel is still on its way out.
 */
const usePushDirection = (depth: number | undefined): number => {
  const previousDepth = useRef(depth)
  const direction = useRef(0)
  const reduceMotion = useReducedMotion()

  if (depth !== previousDepth.current) {
    direction.current =
      depth === undefined || previousDepth.current === undefined
        ? 0
        : Math.sign(depth - previousDepth.current)
    previousDepth.current = depth
  }

  return reduceMotion ? 0 : direction.current
}

export type CalendarPanelProps = Readonly<{
  /**
   * Whether the reader may change what the item panel shows. False draws every
   * field to be read, which is what a view-only reader sees.
   *
   * @default true
   */
  canEdit?: boolean
  className?: string
  /**
   * The filter drawer. The panel only makes room for it — everything about
   * filtering lives in whatever is passed here.
   */
  filtersSlot?: ReactNode
  /** Everything in view. The panels take what they need out of it. */
  items: readonly CalendarItem[]
  locale?: IETFLanguageTag
  onClose: () => void
  /**
   * Saves one field of the item on show. Without it the item panel draws
   * everything to be read.
   */
  onEdit?: OnCalendarItemEdit
  /** Fired by the item panel's "Open", which is the host's job to act on. */
  onNavigate: (item: CalendarItem) => void
  onOpenDay: (date: Date) => void
  onOpenItem: (
    id: string,
    options?: { back?: CalendarPanelStep; tab?: CalendarPanelTab }
  ) => void
  /** What the panel is showing. Comes from `useCalendarPanel`. */
  panel: CalendarPanelState
  /**
   * Items that are not in view but can still be referred to. A hidden moment
   * layer keeps naming the content that belongs to it, and bounding the days
   * that content may move to, through this list.
   */
  referenceItems?: readonly CalendarItem[]
  /**
   * The channel mark the grid draws on an item's chip. The item panel asks for
   * the `field` variant, sized for the icon in front of a field's value.
   */
  renderChannelIcon?: (item: CalendarItem, variant?: 'field') => ReactNode
  /** Draws the way to make something on the day the panel is showing. */
  renderCreate?: (date: Date) => ReactNode
  /**
   * The workflow mark an item has earned, the same one the grid puts on a
   * chip. A host that has been told not to show workflow draws none.
   */
  renderStatusIcon?: (item: CalendarItem, size: number) => ReactNode
  /**
   * The colour an item carries in the label set a person chose to colour by,
   * the same answer the grid draws with. Without it a panel would give an item
   * a different colour from the bar it was opened from.
   */
  resolveChosenColor?: ResolveChosenColor
  resolveDisplay?: ResolveDisplay
  /**
   * Answers the item panel's hero and the tiles on a day's moment rows. Only the
   * host can fetch a picture.
   */
  resolveImageUrl?: ResolveImageUrl
  shouldShowInMarketBars?: boolean
  /** The kinds the grid draws, so a day's summary counts only those. */
  shownKinds?: readonly CalendarItemKind[]
}>

/**
 * The panel beside the month grid. It shows one of three things — the filter
 * drawer, a day, or an item — and slides shut when there is nothing to show.
 *
 * It holds no state of its own: `useCalendarPanel` decides what to show and the
 * host wires the grid's callbacks to it.
 */
export const CalendarPanel: React.FC<CalendarPanelProps> = ({
  canEdit,
  className,
  filtersSlot,
  items,
  locale,
  onClose,
  onEdit,
  onNavigate,
  onOpenDay,
  onOpenItem,
  panel,
  referenceItems,
  renderChannelIcon,
  renderCreate,
  renderStatusIcon,
  resolveChosenColor,
  resolveDisplay,
  resolveImageUrl,
  shouldShowInMarketBars,
  shownKinds,
}) => {
  const formatters = useMemo(() => buildPanelFormatters(locale), [locale])
  const depth = calendarPanelDepth(panel)
  const direction = usePushDirection(depth)

  const item =
    panel?.type === 'item'
      ? items.find((each) => each.id === panel.id)
      : undefined

  // Previous and next walk everything the grid draws, in the order a person
  // reads it, so the panel can be stepped through the whole month.
  const cycleIds = useMemo(
    () => buildGridOrder(items).map((each) => each.id),
    [items]
  )

  // Stepping is sideways: it hands on the way back it was given, so the panel
  // stays where it is on the path. A step out of a day can land on another day,
  // and then the way back follows it there rather than naming the day the panel
  // was first opened from. An item opened straight off the grid had no way back,
  // and stepping does not give it one.
  const stepTo = (id: string) => {
    const back = panel?.type === 'item' ? panel.back : undefined
    const next = items.find((each) => each.id === id)
    const day =
      back?.type === 'day' && next !== undefined
        ? parseCalendarDate(next.start, { field: 'start', id })
        : null

    onOpenItem(id, { back: day === null ? back : { type: 'day', date: day } })
  }

  // Content opened inside a moment is a step deeper than the moment, so it
  // pushes onto it: the way back out of the content is the moment itself.
  const pushChild = (id: string) => {
    if (panel?.type === 'item') onOpenItem(id, { back: panel })
  }

  // The tab a person picks is the host's to remember: the panel is taken down
  // while a piece of the moment's content is on show, and only what the host
  // holds is still there when it is put back up.
  const rememberTab = (tab: CalendarPanelTab) => {
    if (panel?.type === 'item') onOpenItem(panel.id, { back: panel.back, tab })
  }

  // Going back is opening what the path says came before, which the host's own
  // two callbacks already do. The way out of that step goes with it, so the
  // path unwinds one step at a time.
  const goBack = (step: CalendarPanelStep) => {
    if (step.type === 'day') onOpenDay(step.date)
    else onOpenItem(step.id, { back: step.back })
  }

  // Nothing to draw means nothing to open for. Without this an item that a
  // re-fetch has dropped would leave an empty 400px strip sitting on screen.
  const hasContent = isCalendarPanelShowing(panel, {
    hasFilters: Boolean(filtersSlot),
    items,
  })

  return (
    <aside
      aria-label="Calendar details"
      className={classNames(styles.selectors.root, className, {
        [styles.stateVariables.open]: hasContent,
      })}
      css={styles.root}
    >
      {/* Escape from anywhere inside the panel shuts it, so a person can leave
          without hunting for the close button. Escape from a popover drawn in
          a portal still bubbles here through React, and only shuts that.
          @see https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/8f75961d965e47afb88854d324bd32fafde7acfe/docs/rules/no-noninteractive-element-interactions.md#case-this-element-is-catching-bubbled-events-from-elements-that-it-contains */}
      <div
        className={styles.selectors.keyCatcher}
        role="presentation"
        onKeyDown={(event) => {
          if (
            event.key !== 'Escape' ||
            !(event.target instanceof Node) ||
            !event.currentTarget.contains(event.target)
          ) {
            return
          }
          event.stopPropagation()
          onClose()
        }}
      >
        {panel?.type === 'filters' && filtersSlot}

        {/* The old panel stays on its stage while it slides out, so the two
            stages overlap for the length of the push. The stage is keyed by
            how deep down the path it sits, not by what it shows: a step at the
            same depth swaps the contents in place, and only a push or a way
            back builds a second stage. */}
        <AnimatePresence custom={direction} initial={false}>
          {panel?.type === 'day' && (
            <motion.div
              key={depth}
              animate="center"
              className={styles.selectors.stage}
              custom={direction}
              exit="exit"
              initial="enter"
              transition={pushTransition}
              variants={push}
            >
              <DayPanel
                date={panel.date}
                formatters={formatters}
                items={items}
                renderChannelIcon={renderChannelIcon}
                renderCreate={renderCreate}
                renderStatusIcon={renderStatusIcon}
                resolveChosenColor={resolveChosenColor}
                resolveDisplay={resolveDisplay}
                resolveImageUrl={resolveImageUrl}
                shouldShowInMarketBars={shouldShowInMarketBars}
                shownKinds={shownKinds}
                onClose={onClose}
                onItemOpen={onOpenItem}
              />
            </motion.div>
          )}

          {panel?.type === 'item' && item && (
            <motion.div
              key={depth}
              animate="center"
              className={styles.selectors.stage}
              custom={direction}
              exit="exit"
              initial="enter"
              transition={pushTransition}
              variants={push}
            >
              <ItemPanel
                back={panel.back}
                canEdit={canEdit}
                cycleIds={cycleIds}
                formatters={formatters}
                item={item}
                items={items}
                referenceItems={referenceItems}
                renderChannelIcon={renderChannelIcon}
                renderStatusIcon={renderStatusIcon}
                resolveChosenColor={resolveChosenColor}
                resolveDisplay={resolveDisplay}
                resolveImageUrl={resolveImageUrl}
                tab={panel.tab}
                onBack={goBack}
                onClose={onClose}
                onEdit={onEdit}
                onNavigate={onNavigate}
                onOpenChild={pushChild}
                onStep={stepTo}
                onTabChange={rememberTab}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </aside>
  )
}
CalendarPanel.displayName = 'Calendar: CalendarPanel'
